import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table"
import { Input } from "./ui/input"
import { Plus, Save, Edit, Mail, Phone, Hash, Search, ListOrdered, RefreshCw } from "lucide-react"
import { useState } from "react"
import { crearCliente, actualizarCliente, actualizarDatosClienteEnPedidos } from "../services/pruebasService"
import { useTienePermiso } from "../App"
import type { Pedido } from "../types/pedido"

interface Cliente {
  id: string
  nombre: string
  direccion?: string
  correo?: string
  contacto?: string
  telefono?: string
  clave_cliente?: string
  created_at?: string
}

interface EditClienteDialogProps {
  cliente: Cliente
  onSave: (id: string, data: Partial<Cliente>) => Promise<void>
}

function EditClienteDialog({ cliente, onSave }: EditClienteDialogProps) {
  const [nombre, setNombre] = useState(cliente.nombre || '')
  const [clave, setClave] = useState(cliente.clave_cliente || '')
  const [contacto, setContacto] = useState(cliente.contacto || '')
  const [correo, setCorreo] = useState(cliente.correo || '')
  const [telefono, setTelefono] = useState(cliente.telefono || '')
  const [direccion, setDireccion] = useState(cliente.direccion || '')
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    if (!nombre.trim()) return
    setLoading(true)
    setError(null)
    try {
      await onSave(cliente.id, {
        nombre: nombre.trim(),
        clave_cliente: clave.trim() || undefined,
        contacto: contacto.trim() || undefined,
        correo: correo.trim() || undefined,
        telefono: telefono.trim() || undefined,
        direccion: direccion.trim() || undefined,
      })
      setIsOpen(false)
    } catch (err: any) {
      setError(err.message || 'Error al guardar cliente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full mt-4">
          <Edit className="h-4 w-4 mr-2" />
          Editar
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Editar Cliente</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          Modifica la información del cliente.
        </DialogDescription>
        <div className="space-y-4">
          <div>
            <label className="text-muted-foreground">Nombre / Empresa *</label>
            <Input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-muted-foreground">Clave de Cliente</label>
            <Input
              value={clave}
              onChange={(e) => setClave(e.target.value)}
              className="mt-1"
              placeholder="Ej: CLI-001"
            />
          </div>
          <div>
            <label className="text-muted-foreground">Persona de Contacto</label>
            <Input
              value={contacto}
              onChange={(e) => setContacto(e.target.value)}
              className="mt-1"
              placeholder="Nombre del contacto"
            />
          </div>
          <div>
            <label className="text-muted-foreground">Correo electrónico</label>
            <Input
              type="email"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              className="mt-1"
              placeholder="cliente@empresa.com"
            />
          </div>
          <div>
            <label className="text-muted-foreground">Teléfono</label>
            <Input
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              className="mt-1"
              placeholder="Ej: 55 1234 5678"
            />
          </div>
          <div>
            <label className="text-muted-foreground">Dirección</label>
            <Input
              value={direccion}
              onChange={(e) => setDireccion(e.target.value)}
              className="mt-1"
              placeholder="Dirección completa"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setIsOpen(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={!nombre.trim() || loading}>
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface AddClienteDialogProps {
  onAdd: (data: {
    nombre: string
    clave_cliente?: string
    contacto?: string
    correo?: string
    telefono?: string
    direccion?: string
  }) => Promise<void>
}

function AddClienteDialog({ onAdd }: AddClienteDialogProps) {
  const [nombre, setNombre] = useState('')
  const [clave, setClave] = useState('')
  const [contacto, setContacto] = useState('')
  const [correo, setCorreo] = useState('')
  const [telefono, setTelefono] = useState('')
  const [direccion, setDireccion] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    if (!nombre.trim()) return
    setLoading(true)
    setError(null)
    try {
      await onAdd({
        nombre: nombre.trim(),
        clave_cliente: clave.trim() || undefined,
        contacto: contacto.trim() || undefined,
        correo: correo.trim().toLowerCase() || undefined,
        telefono: telefono.trim() || undefined,
        direccion: direccion.trim() || undefined,
      })
      setNombre('')
      setClave('')
      setContacto('')
      setCorreo('')
      setTelefono('')
      setDireccion('')
      setIsOpen(false)
    } catch (err: any) {
      setError(err.message || 'Error al crear cliente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Agregar Cliente
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Agregar Nuevo Cliente</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          Complete los campos para registrar un nuevo cliente.
        </DialogDescription>
        <div className="space-y-4">
          <div>
            <label className="text-muted-foreground">Nombre / Empresa *</label>
            <Input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className="mt-1"
              placeholder="Nombre de la empresa o cliente"
            />
          </div>
          <div>
            <label className="text-muted-foreground">Clave de Cliente</label>
            <Input
              value={clave}
              onChange={(e) => setClave(e.target.value)}
              className="mt-1"
              placeholder="Ej: CLI-001"
            />
          </div>
          <div>
            <label className="text-muted-foreground">Persona de Contacto</label>
            <Input
              value={contacto}
              onChange={(e) => setContacto(e.target.value)}
              className="mt-1"
              placeholder="Nombre del contacto"
            />
          </div>
          <div>
            <label className="text-muted-foreground">Correo electrónico</label>
            <Input
              type="email"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              className="mt-1"
              placeholder="cliente@empresa.com"
            />
          </div>
          <div>
            <label className="text-muted-foreground">Teléfono</label>
            <Input
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              className="mt-1"
              placeholder="Ej: 55 1234 5678"
            />
          </div>
          <div>
            <label className="text-muted-foreground">Dirección</label>
            <Input
              value={direccion}
              onChange={(e) => setDireccion(e.target.value)}
              className="mt-1"
              placeholder="Dirección completa"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setIsOpen(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={!nombre.trim() || loading}>
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Agregando...' : 'Agregar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const AVISO_ACTUALIZACION =
  "Verás el cambio en cuanto vuelvas a abrir este pedido en el sistema. Pero si el reporte de este pedido ya se generó, el PDF ya generado NO se actualiza solo: hay que reabrir el pedido y volver a generar el reporte para que refleje los datos nuevos."

function formatDatosCliente(cliente: Cliente) {
  return {
    nombre_cliente: cliente.nombre,
    telefono: cliente.telefono || undefined,
    correo: cliente.correo || undefined,
    direccion: cliente.direccion || undefined,
  }
}

interface PedidoRowActualizarProps {
  pedido: Pedido
  cliente: Cliente
  onUpdated: () => void
}

function PedidoRowActualizar({ pedido, cliente, onUpdated }: PedidoRowActualizarProps) {
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const handleConfirm = async () => {
    setLoading(true)
    try {
      await actualizarDatosClienteEnPedidos([pedido.id], formatDatosCliente(cliente))
      onUpdated()
      setIsOpen(false)
    } finally {
      setLoading(false)
    }
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" title="Actualizar datos de cliente en este pedido">
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualizar
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>¿Actualizar datos de cliente en el pedido {pedido.numeroPedido}?</AlertDialogTitle>
          <AlertDialogDescription>
            Se sobrescribirán el nombre, teléfono, correo y dirección guardados en este pedido con los datos
            actuales de la ficha de {cliente.nombre}. {AVISO_ACTUALIZACION}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={loading}>
            {loading ? 'Actualizando...' : 'Actualizar'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

interface ActualizarTodosPedidosProps {
  pedidosCliente: Pedido[]
  cliente: Cliente
  onUpdated: () => void
}

function ActualizarTodosPedidos({ pedidosCliente, cliente, onUpdated }: ActualizarTodosPedidosProps) {
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const handleConfirm = async () => {
    setLoading(true)
    try {
      await actualizarDatosClienteEnPedidos(pedidosCliente.map(p => p.id), formatDatosCliente(cliente))
      onUpdated()
      setIsOpen(false)
    } finally {
      setLoading(false)
    }
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>
        <Button size="sm" disabled={pedidosCliente.length === 0}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualizar todos los pedidos
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>¿Actualizar datos de cliente en los {pedidosCliente.length} pedidos de {cliente.nombre}?</AlertDialogTitle>
          <AlertDialogDescription>
            Se sobrescribirán el nombre, teléfono, correo y dirección guardados en cada uno de estos pedidos con los
            datos actuales de la ficha del cliente. {AVISO_ACTUALIZACION}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={loading}>
            {loading ? 'Actualizando...' : 'Actualizar todos'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

interface ClientePedidosDialogProps {
  cliente: Cliente
  pedidos: Pedido[]
  onUpdated: () => void
}

function ClientePedidosDialog({ cliente, pedidos, onUpdated }: ClientePedidosDialogProps) {
  const [busquedaPedido, setBusquedaPedido] = useState('')
  const pedidosCliente = pedidos.filter(p => p.clienteId === cliente.id)
  const pedidosMostrados = pedidosCliente.filter(p =>
    p.numeroPedido?.toLowerCase().includes(busquedaPedido.trim().toLowerCase())
  )

  return (
    <Dialog onOpenChange={(open) => { if (!open) setBusquedaPedido('') }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full mt-2">
          <ListOrdered className="h-4 w-4 mr-2" />
          Ver pedidos
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Pedidos de {cliente.nombre}</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          Pedidos vinculados a este cliente. Actualiza los datos de contacto guardados en cada uno para que
          coincidan con la ficha actual del cliente.
        </DialogDescription>
        <div className="flex justify-between items-center gap-3 shrink-0">
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por No. de pedido..."
              value={busquedaPedido}
              onChange={(e) => setBusquedaPedido(e.target.value)}
              className="pl-9"
            />
          </div>
          <ActualizarTodosPedidos pedidosCliente={pedidosCliente} cliente={cliente} onUpdated={onUpdated} />
        </div>
        {pedidosCliente.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Este cliente no tiene pedidos vinculados.
          </div>
        ) : pedidosMostrados.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Sin resultados para "{busquedaPedido}".
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto -mx-6 px-6 border-t">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead className="whitespace-nowrap">Pedido</TableHead>
                  <TableHead className="whitespace-nowrap">Fecha de emisión</TableHead>
                  <TableHead className="text-right whitespace-nowrap">Acción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pedidosMostrados.map(pedido => (
                  <TableRow key={pedido.id}>
                    <TableCell className="whitespace-nowrap">{pedido.numeroPedido}</TableCell>
                    <TableCell className="whitespace-nowrap">{pedido.fechaEmision}</TableCell>
                    <TableCell className="text-right">
                      <PedidoRowActualizar pedido={pedido} cliente={cliente} onUpdated={onUpdated} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

interface ClientesViewProps {
  clientes: Cliente[]
  pedidos: Pedido[]
  onRefresh?: () => void
}

export function ClientesView({ clientes, pedidos, onRefresh }: ClientesViewProps) {
  const isAdmin = useTienePermiso('editar_contactos')
  const [busqueda, setBusqueda] = useState('')

  const clientesFiltrados = clientes.filter(c => {
    const q = busqueda.toLowerCase()
    return (
      c.nombre?.toLowerCase().includes(q) ||
      c.clave_cliente?.toLowerCase().includes(q) ||
      c.correo?.toLowerCase().includes(q) ||
      c.telefono?.toLowerCase().includes(q)
    )
  })

  const getInitials = (nombre: string) => {
    if (!nombre?.trim()) return '?'
    return nombre.trim().split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const handleAddCliente = async (data: Parameters<typeof crearCliente>[0]) => {
    await crearCliente(data)
    if (onRefresh) onRefresh()
  }

  const handleSaveCliente = async (id: string, data: Partial<Cliente>) => {
    const updateData: Parameters<typeof actualizarCliente>[1] = {}
    if (data.nombre !== undefined) updateData.nombre = data.nombre
    if (data.clave_cliente !== undefined) updateData.clave_cliente = data.clave_cliente
    if (data.contacto !== undefined) updateData.contacto = data.contacto
    if (data.correo !== undefined) updateData.correo = data.correo
    if (data.telefono !== undefined) updateData.telefono = data.telefono
    if (data.direccion !== undefined) updateData.direccion = data.direccion
    await actualizarCliente(id, updateData)
    if (onRefresh) onRefresh()
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1>Clientes</h1>
        {isAdmin && <AddClienteDialog onAdd={handleAddCliente} />}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nombre, clave, correo..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="pl-9"
        />
      </div>

      {clientesFiltrados.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {busqueda ? `Sin resultados para "${busqueda}".` : 'No hay clientes registrados.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clientesFiltrados.map((cliente) => (
            <Card key={cliente.id}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold text-sm">
                    {getInitials(cliente.nombre)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base truncate">{cliente.nombre}</CardTitle>
                    {cliente.clave_cliente && (
                      <Badge variant="outline" className="mt-1 text-xs">
                        <Hash className="h-3 w-3 mr-1" />
                        {cliente.clave_cliente}
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className={`truncate ${!cliente.correo ? 'text-muted-foreground' : ''}`}>
                      {cliente.correo || '—'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className={!cliente.telefono ? 'text-muted-foreground' : ''}>
                      {cliente.telefono || '—'}
                    </span>
                  </div>
                  {isAdmin && (
                    <>
                      <EditClienteDialog
                        cliente={cliente}
                        onSave={handleSaveCliente}
                      />
                      <ClientePedidosDialog
                        cliente={cliente}
                        pedidos={pedidos}
                        onUpdated={() => onRefresh && onRefresh()}
                      />
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
